/**
 * Custom hook for BAA signing/viewing session management
 *
 * Provides unified logic for opening PandaDoc signing or viewing sessions
 * with customizable behavior for different contexts.
 */

import { useCallback, useEffect, useState } from 'react';
import { createLogger } from '@ctrl-enter/tools/utils';

const logger = createLogger('client:hook:use-baa-signing');
import { useAuth } from '../services/auth-service';
import { useElectron } from '../services/electron/electron-provider';
import { useCreateBAASigningSessionMutation, useCheckChatAccessQuery } from './use-gateway-queries';
import { toast } from '../utils/toast';
import { extractErrorMessage } from '../utils/error-helpers';
import { usePandaDocMonitor } from './use-pandadoc-monitor';

interface UseBAASigningOptions {
  /**
   * If true, allows viewing already-signed documents instead of returning an error
   * @default false
   */
  viewIfSigned?: boolean;

  /**
   * If true, shows toast notifications for success/error
   * @default true
   */
  showToasts?: boolean;

  /**
   * Callback when session is successfully opened
   */
  onSuccess?: (mode: 'signing' | 'viewing') => void;

  /**
   * Callback when an error occurs
   */
  onError?: (error: string) => void;
}

export const useBAASigning = ({
  viewIfSigned = false,
  showToasts = true,
  onSuccess,
  onError,
}: UseBAASigningOptions = {}) => {
  const { refreshTokens, user, isAuthenticated } = useAuth();
  const { isElectronAvailable, pandaDoc, external } = useElectron();
  const createSessionMutation = useCreateBAASigningSessionMutation();
  const [isPollingBAA, setIsPollingBAA] = useState(false);

  // Query for checking subscription/BAA status
  const { refetch: refetchChatAccess } = useCheckChatAccessQuery(
    isAuthenticated && !!user?.organizationId
  );

  // CHROME EXTENSION BAA MONITORING (Extension Required for Web!)
  // Monitors PandaDoc page for BAA signing completion
  const pandadocMonitor = usePandaDocMonitor({
    onSuccess: async sessionId => {
      logger.info('✅ [BAA-SIGNING] BAA signing success detected from extension!', { sessionId });

      if (showToasts) {
        toast.success(
          'BAA Signed! 🎉',
          'Your Business Associate Agreement has been signed. Refreshing your access...'
        );
      }

      // Refresh tokens and chat access immediately
      try {
        await refreshTokens();
        await refetchChatAccess();

        // Reload to update UI
        setTimeout(() => {
          location.reload();
        }, 1000);
      } catch (error) {
        logger.error('❌ [BAA-SIGNING] Failed to refresh after BAA signing:', error);
      }
    },
    debug: true,
  });

  // Background polling for BAA signature completion
  useEffect(() => {
    if (!isPollingBAA) return;

    logger.info('🔄 [BAA-SIGNING] Starting BAA signature polling...');

    let pollCount = 0;
    const maxPolls = 60; // 60 attempts = 3 minutes (at 3 seconds each)
    const intervalMs = 3000; // Check every 3 seconds

    const pollInterval = setInterval(async () => {
      pollCount++;
      logger.info(`🔄 [BAA-SIGNING] Polling for BAA signature ${pollCount}/${maxPolls}`);

      try {
        // Refresh tokens to get latest user state
        await refreshTokens();

        // Check subscription status
        const result = await refetchChatAccess();
        const subscriptionStatus = result?.data?.body?.subscription?.status;

        logger.info(`🔍 [BAA-SIGNING] Subscription status: ${subscriptionStatus}`);

        // If status is no longer 'pending', BAA has been signed
        if (subscriptionStatus && subscriptionStatus !== 'pending') {
          logger.info('✅ [BAA-SIGNING] BAA signature detected!');
          clearInterval(pollInterval);
          setIsPollingBAA(false);

          // Show success toast
          toast.success(
            'BAA Signed! 🎉',
            'Your Business Associate Agreement has been signed. You can now access chat!'
          );

          // Refresh the page to update UI
          setTimeout(() => {
            location.reload();
          }, 1000);
          return;
        }
      } catch (error) {
        logger.error('❌ [BAA-SIGNING] Polling error:', error);
      }

      // Stop after max attempts
      if (pollCount >= maxPolls) {
        logger.warn('⚠️ [BAA-SIGNING] Polling timeout after 3 minutes');
        clearInterval(pollInterval);
        setIsPollingBAA(false);

        toast.info(
          'Still Processing',
          'BAA signature is taking longer than expected. Please refresh the page in a few moments.'
        );
      }
    }, intervalMs);

    return () => {
      clearInterval(pollInterval);
    };
  }, [isPollingBAA, refetchChatAccess, refreshTokens]);

  const openBAASession = useCallback(async () => {
    createSessionMutation.mutateAsync(
      {
        body: { viewIfSigned },
      },
      {
        onSuccess: async response => {
          if (response.body.success && response.body.data?.sessionUrl) {
            const mode = response.body.data.mode || 'signing';

            logger.info(`📄 Opening PandaDoc ${mode} session in external window:`, {
              documentId: response.body.data.documentId,
              mode,
            });

            // Check if running in Electron or web
            if (isElectronAvailable && pandaDoc) {
              // Desktop: Use Electron API to open PandaDoc in native window
              try {
                // Set up callback for when the PandaDoc window is closed
                const cleanup = pandaDoc.onCallbackReceived(async data => {
                  if (data.success) {
                    logger.info('The PandaDoc window was closed!');
                    // Only reload if we were signing (not just viewing)
                    if (mode === 'signing') {
                      await refreshTokens();
                      location.reload();
                    }
                    onSuccess?.(mode);
                  }
                });

                // Open the PandaDoc window
                const result = await pandaDoc.openSigningSession(response.body.data.sessionUrl);

                if (!result.success) {
                  logger.error('Failed to open PandaDoc window:', result.error);
                  cleanup();

                  const errorMsg = result.error || 'Unknown error';
                  if (showToasts) {
                    toast.error('Failed to Open', `Could not open BAA ${mode} window.`);
                  }
                  onError?.(errorMsg);
                } else {
                  if (showToasts) {
                    toast.success(
                      'Opening BAA',
                      mode === 'viewing'
                        ? 'Your signed BAA document is opening...'
                        : 'BAA signing window is opening...'
                    );
                  }

                  if (mode === 'signing') {
                    logger.info('✅ PandaDoc signing session opened');
                    logger.info(
                      '🔍 Completion will be detected via webhook - please refresh after signing'
                    );
                  }
                }
              } catch (error) {
                logger.error('Error opening PandaDoc window:', error);
                const errorMsg = extractErrorMessage(error);
                if (showToasts) {
                  toast.error('Error', `Failed to open BAA ${mode} window.`);
                }
                onError?.(errorMsg);
              }
            } else {
              // Web/Extension: Open PandaDoc URL in new browser tab
              logger.info('🌐 Opening PandaDoc session in browser tab');
              try {
                const sessionUrl = response.body.data.sessionUrl;

                // CHROME EXTENSION REQUIRED!
                // Extension will:
                // 1. Intercept navigation to PandaDoc
                // 2. Open in new tab (keeping web app alive)
                // 3. Monitor for BAA signing completion
                // 4. Notify web app immediately
                // 5. Auto-close PandaDoc tab

                if (pandadocMonitor.hasExtension) {
                  logger.info(
                    '✅ [BAA-SIGNING] Extension detected - BAA completion will be automatically detected'
                  );

                  // Use external.openLink for extension compatibility
                  external.openLink(sessionUrl, {
                    width: 1200,
                    height: 900,
                    title: mode === 'viewing' ? 'View BAA Document' : 'Sign BAA Document',
                  });

                  if (showToasts) {
                    toast.success(
                      'Opening BAA',
                      mode === 'viewing'
                        ? 'Your signed BAA document is opening in a new tab...'
                        : "BAA signing window is opening in a new tab. We'll automatically detect when you're done!"
                    );
                  }
                } else {
                  // NO EXTENSION - Fallback to polling (less reliable)
                  logger.warn('⚠️ [BAA-SIGNING] No extension detected - using polling fallback');

                  external.openLink(sessionUrl, {
                    width: 1200,
                    height: 900,
                    title: mode === 'viewing' ? 'View BAA Document' : 'Sign BAA Document',
                  });

                  if (showToasts) {
                    toast.info(
                      'Opening BAA',
                      mode === 'viewing'
                        ? 'Your signed BAA document is opening in a new tab...'
                        : 'BAA signing window is opening. Please refresh the page after signing.'
                    );
                  }

                  if (mode === 'signing') {
                    logger.info('🔄 Starting polling fallback to detect signature completion');
                    setIsPollingBAA(true);
                  }
                }

                onSuccess?.(mode);
                return sessionUrl;
              } catch (error) {
                logger.error('Error opening PandaDoc URL:', error);
                const errorMsg = extractErrorMessage(error);
                if (showToasts) {
                  toast.error('Error', `Failed to open BAA ${mode} window.`);
                }
                onError?.(errorMsg);
              }
            }
          } else {
            logger.info('❌ Failed to create PandaDoc session:', JSON.stringify(response.body));
            const errorMsg = 'Failed to create session';
            if (showToasts) {
              toast.error('Failed', 'Could not create BAA session.');
            }
            onError?.(errorMsg);
          }
        },
        onError: error => {
          const errorMessage = extractErrorMessage(error);
          logger.error('BAA session error:', errorMessage);

          if (showToasts) {
            toast.error('Error', errorMessage);
          }
          onError?.(errorMessage);
        },
      }
    );
  }, [
    viewIfSigned,
    showToasts,
    onSuccess,
    onError,
    createSessionMutation,
    isElectronAvailable,
    pandaDoc,
    external,
    refreshTokens,
  ]);

  return {
    openBAASession,
    isLoading: createSessionMutation.isPending,
    isPolling: isPollingBAA,
  };
};
